/**
 * Картинка, перетащенная из браузера (Яндекс.Картинки и другие страницы).
 *
 * В dataTransfer часто нет файла с типом image/*: у ссылки вида
 * https://avatars.mds.yandex.net/i?id=...&n=13 нет расширения, а text/plain
 * бывает адресом страницы поиска, не самой картинки. Настоящий src лежит
 * в text/html у <img>. Файл без MIME тоже встречается — тип берём по байтам.
 */

const GENERIC_MIME = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
  'application/binary',
]);

const EXT_BY_MIME = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/bmp': '.bmp',
  'video/mp4': '.mp4',
  'video/webm': '.webm',
  'video/quicktime': '.mov',
  'audio/mpeg': '.mp3',
  'audio/wav': '.wav',
  'audio/mp4': '.m4a',
  'audio/ogg': '.ogg',
};

const MIME_BY_EXT = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.bmp': 'image/bmp',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.m4a': 'audio/mp4',
  '.ogg': 'audio/ogg',
};

const MAX_REMOTE_BYTES = 25 * 1024 * 1024;

const MEDIA_SRC_RE = /<(img|video|audio|source)\b[^>]*?\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;
const SRCSET_RE = /\bsrcset\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;

function decodeHtmlEntities(value) {
  return String(value)
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
}

function safeGetData(dataTransfer, type) {
  try {
    return dataTransfer.getData(type) || '';
  } catch {
    return '';
  }
}

export function sniffMediaMime(bytes) {
  if (!bytes || bytes.length < 3) return '';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png';
  }
  if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return 'image/gif';
  }
  if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d) return 'image/bmp';
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46
  ) {
    const tag = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
    if (tag === 'WEBP') return 'image/webp';
    if (tag === 'WAVE') return 'audio/wav';
  }
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x1a &&
    bytes[1] === 0x45 &&
    bytes[2] === 0xdf &&
    bytes[3] === 0xa3
  ) {
    return 'video/webm';
  }
  if (
    bytes.length >= 12 &&
    String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]) === 'ftyp'
  ) {
    return 'video/mp4';
  }
  if (bytes.length >= 3 && bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
    return 'audio/mpeg';
  }
  return '';
}

function readPrefix(file, length) {
  const slice = file.slice(0, length);
  if (slice && typeof slice.arrayBuffer === 'function') {
    return slice.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsArrayBuffer(slice);
  });
}

function mimeFromName(name) {
  const lower = String(name || '').toLowerCase();
  const ext = Object.keys(MIME_BY_EXT).find((key) => lower.endsWith(key));
  return ext ? MIME_BY_EXT[ext] : '';
}

function withExtension(name, mime) {
  const ext = EXT_BY_MIME[mime] || '';
  const cleaned = String(name || 'file').split('?')[0].split('/').pop() || 'file';
  if (!ext) return cleaned;
  const lower = cleaned.toLowerCase();
  if (lower.endsWith(ext) || (ext === '.jpg' && lower.endsWith('.jpeg'))) return cleaned;
  const stem = cleaned.replace(/\.[a-z0-9]{1,8}$/i, '') || 'image';
  return `${stem}${ext}`;
}

export async function normalizeMediaFile(file) {
  if (!file || !file.size) return null;
  const declared = String(file.type || '').toLowerCase().split(';')[0].trim();
  let sniffed = '';
  try {
    sniffed = sniffMediaMime(new Uint8Array(await readPrefix(file, 32)));
  } catch {
    sniffed = '';
  }
  const mime = sniffed || (GENERIC_MIME.has(declared) ? mimeFromName(file.name) : declared);
  if (!mime.startsWith('image/') && !mime.startsWith('video/') && !mime.startsWith('audio/')) {
    return null;
  }
  const name = withExtension(file.name, mime);
  if (file.name === name && declared === mime) return file;
  return new File([file], name, { type: mime, lastModified: file.lastModified });
}

function matchesKind(file, kind) {
  const type = file.type || '';
  if (kind === 'video') return type.startsWith('video/');
  if (kind === 'audio') return type.startsWith('audio/');
  return type.startsWith('image/');
}

function fileFromEntry(entry) {
  return new Promise((resolve) => {
    if (!entry || entry.isDirectory || typeof entry.file !== 'function') {
      resolve(null);
      return;
    }
    entry.file(
      (file) => resolve(file || null),
      () => resolve(null)
    );
  });
}

export function captureDropPayload(dataTransfer) {
  const files = [];
  const entries = [];
  const pushFile = (file) => {
    if (!file) return;
    const key = `${file.name}:${file.size}:${file.lastModified}`;
    if (files.some((existing) => `${existing.name}:${existing.size}:${existing.lastModified}` === key)) {
      return;
    }
    files.push(file);
  };

  if (dataTransfer.files) {
    Array.from(dataTransfer.files).forEach(pushFile);
  }
  if (dataTransfer.items) {
    Array.from(dataTransfer.items).forEach((item) => {
      if (item.kind !== 'file') return;
      if (typeof item.getAsFile === 'function') pushFile(item.getAsFile());
      if (typeof item.webkitGetAsEntry === 'function') {
        const entry = item.webkitGetAsEntry();
        if (entry && entry.isFile) entries.push(entry);
      }
    });
  }

  return {
    reorder: safeGetData(dataTransfer, 'application/reorder') === 'true',
    html: safeGetData(dataTransfer, 'text/html'),
    uriList: safeGetData(dataTransfer, 'text/uri-list'),
    plain: safeGetData(dataTransfer, 'text/plain'),
    json: safeGetData(dataTransfer, 'application/json'),
    moz: safeGetData(dataTransfer, 'text/x-moz-url'),
    files,
    entries,
  };
}

export function normalizeDropUrl(value) {
  if (!value || typeof value !== 'string') return '';
  let url = decodeHtmlEntities(value.trim());
  if (!url || url.startsWith('#')) return '';
  if (url.startsWith('//')) url = `https:${url}`;
  if (/^file:/i.test(url)) return url;
  if (url.startsWith('/static/')) return url;
  if (/^data:(image|video|audio)\//i.test(url)) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return '';
}

function extractHtmlMediaSrcs(html) {
  if (!html) return [];
  const urls = [];
  const srcRe = new RegExp(MEDIA_SRC_RE.source, 'gi');
  let match = srcRe.exec(html);
  while (match) {
    const raw = match[2] || match[3] || match[4] || '';
    if (raw && !raw.startsWith('data:image/svg')) urls.push(raw);
    match = srcRe.exec(html);
  }
  const srcsetRe = new RegExp(SRCSET_RE.source, 'gi');
  match = srcsetRe.exec(html);
  while (match) {
    const raw = match[1] || match[2] || '';
    const first = raw.split(',')[0].trim().split(/\s+/)[0];
    if (first) urls.push(first);
    match = srcsetRe.exec(html);
  }
  return urls;
}

function plainUrlCandidates(text) {
  if (!text) return [];
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const direct = lines.filter((line) => /^(https?:\/\/|data:|file:|\/static\/|\/\/)/i.test(line));
  if (direct.length) return direct;
  const embedded = text.match(/https?:\/\/[^\s<>"']+/i);
  return embedded ? [embedded[0].replace(/[),.;]+$/, '')] : [];
}

export function collectDropUrls(payload) {
  const ordered = [];
  const push = (url, source) => {
    const clean = normalizeDropUrl(url);
    if (!clean || ordered.some((item) => item.url === clean)) return;
    ordered.push({ url: clean, source });
  };

  extractHtmlMediaSrcs(payload.html).forEach((url) => push(url, 'media'));

  if (payload.json) {
    try {
      const data = JSON.parse(payload.json);
      if (data && typeof data.url === 'string') push(data.url, 'json');
    } catch {
      // чужой JSON в перетаскивании — не адрес картинки
    }
  }

  String(payload.uriList || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'))
    .forEach((url) => push(url, 'uri'));

  if (payload.moz) push(String(payload.moz).split(/\r?\n/)[0], 'uri');
  plainUrlCandidates(payload.plain).forEach((url) => push(url, 'plain'));
  return ordered;
}

function isDirectMediaUrl(url, kind) {
  if (/^data:/i.test(url)) {
    if (kind === 'video') return /^data:video\//i.test(url);
    if (kind === 'audio') return /^data:audio\//i.test(url);
    return /^data:image\//i.test(url);
  }
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  const imageExt = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'];
  const videoExt = ['.mp4', '.webm', '.mov', '.m4v'];
  const audioExt = ['.mp3', '.wav', '.m4a', '.ogg', '.aac', '.flac'];
  const list = kind === 'video' ? videoExt : kind === 'audio' ? audioExt : imageExt;
  return list.some((ext) => path.endsWith(ext));
}

function isAppMediaUrl(url) {
  return url.startsWith('/static/') || url.includes('/static/uploads/') || url.includes('/static/results/');
}

function looksLikeDocumentUrl(url) {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.toLowerCase();
    if (path.endsWith('.html') || path.endsWith('.htm') || path.endsWith('.php') || path.endsWith('/')) {
      return true;
    }
    const host = parsed.hostname.toLowerCase();
    if (host === 'yandex.ru' || host.endsWith('.yandex.ru') || host === 'google.com' || host.endsWith('.google.com') || host.endsWith('.google.ru')) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function fallbackMatches(item, kind) {
  if (isAppMediaUrl(item.url) || item.source === 'json') return true;
  if (isDirectMediaUrl(item.url, kind)) return true;
  if (item.source === 'media' && kind === 'image') return true;
  return false;
}

function nameFromUrl(url, mime) {
  try {
    if (/^data:/i.test(url)) return withExtension('image', mime);
    const path = new URL(url, 'https://local.invalid').pathname;
    const base = decodeURIComponent(path.split('/').filter(Boolean).pop() || 'image');
    return withExtension(base, mime);
  } catch {
    return withExtension('image', mime);
  }
}

function fileFromDataUrl(url) {
  const match = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(url);
  if (!match) return null;
  const mime = (match[1] || 'application/octet-stream').toLowerCase();
  const isBase64 = Boolean(match[2]);
  const data = match[3] || '';
  let bytes;
  try {
    if (isBase64) {
      const binary = atob(data);
      bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(data));
    }
  } catch {
    return null;
  }
  return normalizeMediaFile(new File([bytes], withExtension('image', mime), { type: mime }));
}

async function fetchUrlAsFile(url, fetchImpl) {
  if (/^data:/i.test(url)) return fileFromDataUrl(url);
  if (!/^https?:\/\//i.test(url) && !url.startsWith('/static/')) return null;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 20000) : null;
  try {
    const response = await fetchImpl(url, {
      method: 'GET',
      credentials: 'omit',
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      signal: controller ? controller.signal : undefined,
    });
    if (!response || !response.ok) return null;
    const headerType = String(response.headers?.get?.('content-type') || '').split(';')[0].trim().toLowerCase();
    if (headerType.startsWith('text/html') || headerType.startsWith('text/plain')) return null;
    const length = Number(response.headers?.get?.('content-length') || 0);
    if (length > MAX_REMOTE_BYTES) return null;
    const blob = await response.blob();
    if (!blob || blob.size === 0 || blob.size > MAX_REMOTE_BYTES) return null;
    const mime = (blob.type && !GENERIC_MIME.has(blob.type) ? blob.type : headerType) || 'application/octet-stream';
    return normalizeMediaFile(new File([blob], nameFromUrl(url, mime), { type: mime }));
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function localDroppedFiles(payload, kind) {
  let incoming = (payload.files || []).filter((file) => file && file.size > 0);
  if (incoming.length === 0 && payload.entries?.length) {
    for (const entry of payload.entries) {
      const file = await fileFromEntry(entry);
      if (file && file.size > 0) incoming.push(file);
    }
  }
  const files = [];
  for (const file of incoming) {
    const normalized = await normalizeMediaFile(file);
    if (normalized && matchesKind(normalized, kind)) files.push(normalized);
  }
  return files;
}

export async function resolveDroppedMedia(payload, kind, fetchImpl = fetch) {
  const files = await localDroppedFiles(payload, kind);
  if (files.length) return { files, fallbackUrl: null, fallbackSource: null, fileProtocol: false };

  const urls = collectDropUrls(payload);
  if (urls.length > 0 && urls.every((item) => /^file:/i.test(item.url))) {
    return { files: [], fallbackUrl: null, fallbackSource: null, fileProtocol: true };
  }

  const httpUrls = urls.filter((item) => !/^file:/i.test(item.url));
  for (const item of httpUrls) {
    const file = await fetchUrlAsFile(item.url, fetchImpl);
    if (file && matchesKind(file, kind)) {
      return { files: [file], fallbackUrl: null, fallbackSource: null, fileProtocol: false };
    }
  }

  const fallback = httpUrls.find((item) => fallbackMatches(item, kind));
  if (fallback) {
    return { files: [], fallbackUrl: fallback.url, fallbackSource: fallback.source, fileProtocol: false };
  }

  const plainHttp = httpUrls.filter((item) => /^https?:\/\//i.test(item.url) && item.source === 'plain');
  if (kind === 'image' && plainHttp.length === 1 && !looksLikeDocumentUrl(plainHttp[0].url)) {
    return { files: [], fallbackUrl: plainHttp[0].url, fallbackSource: 'plain', fileProtocol: false };
  }

  return { files: [], fallbackUrl: null, fallbackSource: null, fileProtocol: false };
}
